import { useEffect, useCallback, useState } from 'react';
import { useAtom } from 'jotai';
import { useAtomValue } from 'jotai';
import { showLibraryPanelAtom, libraryLayersAtom, libraryFiltersAtom, libraryPaginationAtom, libraryLoadingAtom, libraryErrorAtom, LibraryMapData } from './store';
import { UI_Header, UI_HeaderLeft, UI_HeaderRight, UI_HeaderRight_item } from '../PanelStyle';
import { useTranslation } from 'react-i18next';
import { memo } from 'react';
import { styled } from 'styled-components';
import { Input, Spin, Empty, Button, Drawer, Checkbox, DatePicker, Pagination } from 'antd';
import { SearchOutlined, FilterOutlined, ReloadOutlined } from '@ant-design/icons';
import { PiBooks } from 'react-icons/pi';
import { FaXmark } from 'react-icons/fa6';
import LibraryItem from './LibraryItem';
import { MapDataAPIs } from '~/common/typescript/apis/MapDataAPIs';
import { ShareMapDataProjectAPIs } from '~/common/typescript/apis/ShareMapDataProjectAPIs';
import { MapData_GetList_RequestQuery } from '~/common/typescript/models/MapData';
import { currentProjectAtom } from '~/stores/ProjectStore';
import { MapData_Base } from '~/common/typescript/models/MapData';
import { message } from 'antd';
import { PanelZIndex } from '~/config/constants/PanelConstants';

const { RangePicker } = DatePicker;

const LibraryPanel = () => {
  const { t } = useTranslation();
  const [showLibraryPanel, setShowLibraryPanel] = useAtom(showLibraryPanelAtom);
  const [layers, setLayers] = useAtom(libraryLayersAtom);
  const [filters, setFilters] = useAtom(libraryFiltersAtom);
  const [pagination, setPagination] = useAtom(libraryPaginationAtom);
  const [loading, setLoading] = useAtom(libraryLoadingAtom);
  const [error, setError] = useAtom(libraryErrorAtom);
  const currentProject = useAtomValue(currentProjectAtom);
  
  const [showFilterDrawer, setShowFilterDrawer] = useState(false);

  const fetchLayers = useCallback(async (reset = true, targetPage?: number, targetPageSize?: number) => {
    const page = reset ? (targetPage || 1) : (targetPage || pagination.page);
    const pageSize = targetPageSize || pagination.pageSize;

    setLoading(true);
    setError(null);

    try {
      const apiFilters: MapData_GetList_RequestQuery = {
        page,
        pageSize,
        keyword: filters.searchText || undefined,
        dataTypes: filters.dataTypes?.length > 0 ? filters.dataTypes.join(',') : undefined,
        processStatus: filters.statuses?.length > 0 ? filters.statuses.join(',') : undefined,
        category: filters.categories?.length > 0 ? filters.categories.join(',') : undefined,
      };

      const response = await MapDataAPIs.remote_mapData_getList(apiFilters);
      
      const results: LibraryMapData[] = response.results || [];
      
      setLayers(reset ? results : [...layers, ...results]);
      setPagination(prev => ({
        ...prev,
        page: response.pagination.page,
        pageSize: response.pagination.pageSize,
        total: response.pagination.total,
        hasMore: prev.page * prev.pageSize < response.pagination.total,
      }));
    } catch (err: any) {
      console.error('Error fetching library layers:', err);
      setError(err.message || t('Không thể tải danh sách layers'));
      message.error(t('Không thể tải danh sách layers'));
    } finally {
      setLoading(false);
    }
  }, [filters, pagination.page, pagination.pageSize, layers, setLayers, setPagination, setLoading, setError, t]);

  // Fetch on open
  useEffect(() => {
    if (showLibraryPanel && layers.length === 0) {
      fetchLayers(true);
    }
  }, [showLibraryPanel]);

  const handleSearch = (value: string) => {
    setFilters(prev => ({ ...prev, searchText: value }));
    fetchLayers(true);
  };

  const handlePageChange = (page: number, pageSize: number) => {
    fetchLayers(true, page, pageSize);
  };

  const handleRetry = () => {
    fetchLayers(true);
  };

  const handleAddToProject = async (item: MapData_Base) => {
    if (!currentProject) {
      message.warning(t('Vui lòng chọn dự án trước'));
      return;
    }

    try {
      await ShareMapDataProjectAPIs.remote_addMapDataToProject({
        projectId: currentProject.id,
        mapDataId: item.id,
      });
      message.success(t('Đã thêm layer vào dự án'));
    } catch (err) {
      console.error('Error adding to project:', err);
      message.error(t('Không thể thêm layer vào dự án'));
    }
  };

  const handleFilterChange = (key: 'dataTypes' | 'statuses', values: string[]) => {
    setFilters(prev => ({ ...prev, [key]: values }));
  };

  const applyFilters = () => {
    setShowFilterDrawer(false);
    fetchLayers(true);
  };

  const clearFilters = () => {
    setFilters({
      searchText: '',
      dataTypes: [],
      categories: [],
      statuses: [],
      ownerId: null,
      dateRange: null,
    });
    fetchLayers(true);
  };

  if (!showLibraryPanel) return null;

  const hasActiveFilters = (filters.dataTypes?.length || 0) > 0 || (filters.statuses?.length || 0) > 0;

  return (
    <UI_Wrapper>
      <UI_Header>
        <UI_HeaderLeft style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <PiBooks style={{ fontSize: '18px', color: '#1677ff' }} />
          <span>{t('Thư viện lớp bản đồ')}</span>
        </UI_HeaderLeft>
        <UI_HeaderRight>
          <UI_HeaderRight_item onClick={() => setShowLibraryPanel(false)} title="Đóng">
            <FaXmark />
          </UI_HeaderRight_item>
        </UI_HeaderRight>
      </UI_Header>

      <SearchBar>
        <SearchInput
          placeholder={t('Tìm kiếm layer...')}
          prefix={<SearchOutlined />}
          allowClear
          onSearch={handleSearch}
          style={{ flex: 1 }}
        />
        <FilterButton 
          type={hasActiveFilters ? "primary" : "default"}
          icon={<FilterOutlined />}
          onClick={() => setShowFilterDrawer(true)}
        >
          {t('Lọc')}
          {hasActiveFilters && <FilterBadge>{(filters.dataTypes?.length || 0) + (filters.statuses?.length || 0)}</FilterBadge>}
        </FilterButton>
        <ReloadButton icon={<ReloadOutlined />} onClick={handleRetry} loading={loading} />
      </SearchBar>

      <ContentArea>
        {error ? (
          <ErrorState>
            <ErrorText>{error}</ErrorText>
            <RetryButton onClick={handleRetry}>{t('Thử lại')}</RetryButton>
          </ErrorState>
        ) : loading && layers.length === 0 ? (
          <LoadingState>
            <Spin size="large" />
            <LoadingText>{t('Đang tải...')}</LoadingText>
          </LoadingState>
        ) : layers.length === 0 ? (
          <EmptyState>
            <Empty description={t('Không có layer nào')} />
          </EmptyState>
        ) : (
          <>
            <LayerGrid>
              {layers.map(layer => (
                <LibraryItem
                  key={layer.id}
                  item={layer}
                  onAdd={handleAddToProject}
                />
              ))}
            </LayerGrid>
            <PaginationArea>
              <Pagination
                current={pagination.page}
                pageSize={pagination.pageSize}
                total={pagination.total}
                onChange={handlePageChange}
                showSizeChanger
                showTotal={(total) => `${t('Tổng')} ${total} ${t('items')}`}
                locale={{ items_per_page: '/ trang' }}
              />
            </PaginationArea>
          </>
        )}
      </ContentArea>

      <FilterDrawer
        title={t('Bộ lọc')}
        placement="right"
        open={showFilterDrawer}
        onClose={() => setShowFilterDrawer(false)}
        footer={
          <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
            <Button onClick={clearFilters}>{t('Xóa lọc')}</Button>
            <Button type="primary" onClick={applyFilters}>{t('Áp dụng')}</Button>
          </div>
        }
      >
        <FilterSection>
          <FilterTitle>{t('Loại dữ liệu')}</FilterTitle>
          <Checkbox.Group
            value={filters.dataTypes}
            onChange={(values) => handleFilterChange('dataTypes', values as string[])}
          >
            <Checkbox value="Vector">Vector</Checkbox>
            <Checkbox value="Raster">Raster</Checkbox>
            <Checkbox value="Point">Point</Checkbox>
            <Checkbox value="LineString">Line</Checkbox>
            <Checkbox value="Polygon">Polygon</Checkbox>
          </Checkbox.Group>
        </FilterSection>

        <FilterSection>
          <FilterTitle>{t('Trạng thái')}</FilterTitle>
          <Checkbox.Group
            value={filters.statuses}
            onChange={(values) => handleFilterChange('statuses', values as string[])}
          >
            <Checkbox value="SUCCESS">{t('Thành công')}</Checkbox>
            <Checkbox value="PENDING">{t('Đang chờ')}</Checkbox>
            <Checkbox value="PROCESSING">{t('Đang xử lý')}</Checkbox>
            <Checkbox value="FAILED">{t('Thất bại')}</Checkbox>
          </Checkbox.Group>
        </FilterSection>
      </FilterDrawer>
    </UI_Wrapper>
  );
};

export default memo(LibraryPanel);

const UI_Wrapper = styled.div`
  position: absolute;
  top: var(--header-height);
  left: 0;
  right: 0;
  z-index: ${PanelZIndex.HIGH};
  background: white;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  max-height: 90vh;
  overflow: hidden;
  display: flex;
  flex-direction: column;
`;

const SearchBar = styled.div`
  display: flex;
  gap: 8px;
  padding: 12px;
  border-bottom: 1px solid #e0e0e0;
`;

const SearchInput = styled(Input.Search)`
  flex: 1;
`;

const FilterButton = styled(Button)`
  display: flex;
  align-items: center;
  gap: 4px;
  white-space: nowrap;
`;

const FilterBadge = styled.span`
  background: var(--color-primary);
  color: white;
  border-radius: 10px;
  padding: 0 6px;
  font-size: 10px;
  margin-left: 4px;
`;

const ReloadButton = styled(Button)`
  width: 36px;
  padding: 0;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const ContentArea = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: 12px;
`;

const LayerGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
  gap: 12px;
`;

const LoadingState = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 200px;
  gap: 12px;
`;

const LoadingText = styled.div`
  color: #666;
`;

const EmptyState = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  height: 200px;
`;

const ErrorState = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 200px;
  gap: 12px;
`;

const ErrorText = styled.div`
  color: #ff4d4f;
  text-align: center;
`;

const RetryButton = styled(Button)`
  margin-top: 8px;
`;

const PaginationArea = styled.div`
  display: flex;
  justify-content: center;
  padding: 16px 0;
  border-top: 1px solid #f0f0f0;
  margin-top: 12px;
`;

const FilterDrawer = styled(Drawer)`
  .ant-drawer-body {
    padding: 16px;
    display: flex;
    flex-direction: column;
    gap: 24px;
  }
`;

const FilterSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const FilterTitle = styled.div`
  font-weight: 600;
  font-size: 14px;
`;
